import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Shield, Brain, Sparkles } from 'lucide-react';
import { tracking } from '../../utils/tracking';

export function AnxietyCoachPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'AI Coach for Anxiety - Manage Stress & Worry | VZMO';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Manage anxiety and stress with AI coaching. Learn coping strategies, work through worries, and build emotional resilience. Private support available 24/7. Start free.'
      );
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://vzmo.ai/coach/anxiety');
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = 'https://vzmo.ai/coach/anxiety';
      document.head.appendChild(link);
    }

    window.scrollTo(0, 0);
  }, []);

  const handleStartFree = () => {
    tracking.startFreeClicked('anxiety-coach-page');
    navigate('/signup');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            AI Coach for Anxiety & Stress
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Learn to manage anxiety, work through worries, and build emotional resilience with AI coaching.
            Private support available whenever you need it.
          </p>
        </div>

        <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-12">
          <p className="text-amber-900">
            <strong>Important:</strong> VZMO is a coaching tool, not a replacement for mental health treatment.
            If you're experiencing severe anxiety or mental health crisis, please contact a licensed therapist
            or call the 988 Suicide & Crisis Lifeline.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12 mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">
            How VZMO Helps with Anxiety
          </h2>

          <div className="space-y-6">
            <div className="flex items-start">
              <Brain className="w-6 h-6 text-[#E07A5F] mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Identify Anxiety Triggers</h3>
                <p className="text-slate-600">
                  Journal about anxious moments and patterns. Over time, recognize what triggers your
                  anxiety and develop personalized coping strategies.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <Shield className="w-6 h-6 text-[#E07A5F] mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Work Through Worries</h3>
                <p className="text-slate-600">
                  Use guided prompts to examine anxious thoughts. Separate realistic concerns from
                  catastrophic thinking and develop action plans for what you can control.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <Heart className="w-6 h-6 text-[#E07A5F] mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Build Coping Skills</h3>
                <p className="text-slate-600">
                  Learn and practice evidence-based techniques like cognitive reframing, grounding
                  exercises, and mindful self-reflection.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <Sparkles className="w-6 h-6 text-[#E07A5F] mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Track Your Progress</h3>
                <p className="text-slate-600">
                  See patterns over time. Notice what strategies work best for you and celebrate
                  improvements in managing anxiety.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Common Anxiety Topics</h3>
            <ul className="space-y-3 text-slate-600">
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Work stress and deadlines
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Social anxiety situations
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Decision-making paralysis
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Future planning worries
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Relationship concerns
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Financial stress
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Techniques You'll Learn</h3>
            <ul className="space-y-3 text-slate-600">
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Cognitive reframing exercises
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Grounding and mindfulness
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Worry time scheduling
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Progressive muscle relaxation
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Journaling for clarity
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Building stress tolerance
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#E07A5F] to-[#F4A261] rounded-2xl p-8 sm:p-12 text-white text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">Start Managing Anxiety Better</h2>
          <p className="text-lg mb-6 opacity-90">
            Get private, judgment-free support. Try VZMO free for 7 days.
          </p>
          <button
            onClick={handleStartFree}
            className="bg-white text-[#E07A5F] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-slate-50 transition-colors"
          >
            Start Free Trial
          </button>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Important Information</h2>
          <div className="space-y-4 text-slate-600">
            <p>
              VZMO provides coaching and self-reflection tools based on evidence-based cognitive behavioral
              techniques. However, it is not a substitute for professional mental health treatment.
            </p>
            <p>
              If you're experiencing severe anxiety, panic attacks, or thoughts of self-harm, please
              seek help from a licensed mental health professional immediately.
            </p>
            <p>
              <strong>Crisis Resources:</strong> If you're in crisis, call 988 (Suicide & Crisis Lifeline)
              or text HOME to 741741 (Crisis Text Line) for immediate support.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
